import { Metric } from "@/entities/Finance/Finance.model";
import { PeriodKind } from "@/entities/Labeling/Account.model";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(quarterOfYear);

interface PeriodColumn {
    field: string;
    headerName: string;
    headerComponent?: any;
    headerComponentParams?: any;
}

export function formatPeriodHeader(period: string): string {
    const date = dayjs(period);
    if (!date.isValid()) return period;

    if (period.length === 4) {
        return period;
    } else if (period.includes("Q")) {
        return period.replace("-", "-").slice(0, -2);
    } else {
        return date.format("MM-YY");
    }
}

export function formatPeriodLong(period: string): string {
    if (period.length === 4) {
        return period;
    }

    if (period.includes("Q")) {
        const match = period.match(/^(\d{4})-?(Q\d)$|^(Q\d)-(\d{2})$/);
        if (match) {
            const quarter = match[2] || match[3];
            const year = match[1] || `20${match[4]}`;
            return `${quarter} ${year}`;
        }
        return period;
    }

    const date = dayjs(period);
    if (!date.isValid()) return period;
    return date.format("MMMM YYYY");
}

export function getPeriodRange(periods: string[]): string {
    const parsed = periods.map(formatPeriodLong);
    if (parsed.length === 1) return parsed[0];
    return `${parsed[0]} - ${parsed[parsed.length - 1]}`;
}

export function sortPeriods(periods: string[]): string[] {
    return periods.sort((a, b) => a.localeCompare(b));
}

export function formatPeriods(periods: string[]): string[] {
    return periods.map(formatPeriodLong);
}
export function getPeriodColumns(periods: string[]): PeriodColumn[] {
    return periods.map((period) => ({
        field: period,
        headerName: formatPeriodHeader(period),
    }));
}

export function getUniquePeriods(metrics: Metric[]): string[] {
    return sortPeriods([
        ...new Set(
            metrics
                .map((metric) => metric.values.map((value) => value.period))
                .flat(),
        ),
    ]);
}

function isEndOfMonth(date: dayjs.Dayjs): boolean {
    return date.isSame(date.endOf("month"), "day");
}

function isEndOfQuarterMonth(date: dayjs.Dayjs): boolean {
    return date.month() === date.endOf("quarter").month();
}

export function getPeriodsForWindow(
    asOfDate: string,
    periodKind: PeriodKind,
    windowSize: number,
): string[] {
    const date = dayjs(asOfDate);
    if (!date.isValid() || windowSize <= 0) return [];

    const periods: string[] = [];

    if (periodKind === PeriodKind.MONTHLY) {
        let current = isEndOfMonth(date)
            ? date.endOf("month")
            : date.subtract(1, "month").endOf("month");
        for (let i = 0; i < windowSize; i++) {
            periods.unshift(current.format("YYYY-MM"));
            current = current.subtract(1, "month").endOf("month");
        }
    } else if (periodKind === PeriodKind.QUARTERLY) {
        let current = isEndOfQuarterMonth(date)
            ? date.endOf("quarter")
            : date.subtract(1, "quarter").endOf("quarter");
        for (let i = 0; i < windowSize; i++) {
            const year = current.year();
            const quarter = current.quarter();
            periods.unshift(`${year}-Q${quarter}`);
            current = current.subtract(1, "quarter").endOf("quarter");
        }
    } else if (periodKind === PeriodKind.YEARLY) {
        let current = date.subtract(1, "year");
        for (let i = 0; i < windowSize; i++) {
            periods.unshift(current.format("YYYY"));
            current = current.subtract(1, "year");
        }
    }

    return periods;
}

export function getYTDPeriods(periods: string[], asOfDate: string): string[] {
    if (periods.length === 0) return [];
    const year = asOfDate.slice(0, 4);
    return periods.filter(
        (period) => period.includes(year) && period <= asOfDate,
    );
}

export function getAllAvailablePeriods(
    periods: string[],
    asOfDate: string,
): string[] {
    if (periods.length === 0) return [];
    return periods.filter((period) => period <= asOfDate);
}
